/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from '@env/environment';
import { AppService } from '@app/services/api-services/apps/app.service';
import { AppModel } from '@app/models/app.model';
import { Subscription } from 'rxjs';
import { SubSink } from '@base/node_modules/subsink/dist/subsink';
import { extract } from '@app/services/i18n/i18n.service';

@Component({
  selector: 'app-app-loader',
  templateUrl: './app-loader.component.html',
  styleUrls: ['./app-loader.component.scss'],
})
export class AppLoaderComponent implements OnInit, OnDestroy {
  private app: AppModel;
  public slug = '';
  public appName = '';
  public projectSlug: string;
  public loading = true;
  public heading = 'Loading';
  public subheading = '';
  public icon = 'icon-gradient bg-tempting-azure';
  public dashId = '';

  // Subscriptions
  private appSubscription: Subscription;
  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private subscriptionContainer = new SubSink();

  constructor(
    private route: ActivatedRoute,
    public titleService: Title,
    private appService: AppService
  ) {}

  /**
   *  Warren Dowey
   *
   *  ngOnInit
   *
   *  Called on init. Gets the app details to pass slugs to the frame component
   * using the app and project-analytics slug passed as vars in the url, this was refactored to make
   * the frame a sub component so that it could be used in several places
   *
   * @returns void
   */
  ngOnInit() {
    this.slug = this.route.snapshot.paramMap.get('slug');
    this.projectSlug = this.route.snapshot.paramMap.get('projectSlug');
    this.subscriptionContainer.sink = this.appSubscription = this.appService.dataSubject.subscribe(
      (data) => {
        this.handleData(data);
        if (this.appSubscription) {
          this.appSubscription.unsubscribe();
        }
      },
      (error) => this.handleError(error)
    );
    this.appService.getAppFromListBySlug(this.slug);
  }

  /**
   *  Warren Dowey
   *
   *  handleData
   *
   *  Handle the database content received for a specfic to app so it can set the view titles
   *
   * @params data: The passed in app data
   *
   * @returns void
   */
  private handleData(data: any) {
    const app = new AppModel();
    if (data[0]) {
      this.app = app.fromJson(data[0]);
      this.appName = this.app.appName;
      this.titleService.setTitle(`${environment.product_name} :: ${extract(this.appName)}`);
      this.heading = extract(this.appName);
      this.subheading = extract(this.app.appDescription);
    } else if (typeof data === 'object') {
      this.app = data;
      this.appName = this.app.appName;
      this.subheading = extract(this.app.appDescription);
      this.titleService.setTitle(`${environment.product_name} :: ${extract(this.appName)}`);
      this.heading = extract(this.appName);
    } else {
      this.appName = 'App not found';
      this.titleService.setTitle(`${environment.product_name} :: ${extract(this.appName)}`);
      this.heading = extract(this.appName);
      this.subheading = extract(
        `app not found on this project or you don't have permissions to view.`
      );
    }
  }
  /**
   *  Warren Dowey
   *
   *  handleError
   *
   *  Handle error when the appLoader cannot load an app.
   *
   * @params error: the error passed in
   *
   * @returns void
   */
  private handleError(error: any) {
    this.appName = `An error has occurred ${error}`;
    this.titleService.setTitle(
      `${environment.product_name} :: ${extract('An error has occurred.')}`
    );
    this.heading = 'An error has occurred';
  }

  ngOnDestroy() {
    this.subscriptionContainer.unsubscribe();
  }
}
